// Gestão de alojamentos: contrato, inventário e custos. Protótipo: index.html:3990–4150 e buildGestao.
// Valores em reais como number (o banco guarda numeric(12,2); a soma segue a ordem do protótipo).

import { chaveCompetencia, competenciaDe } from './competencia.js';
import type { Competencia, DataCivil, ObraCadastro } from './tipos.js';

export type TipoGasto = 'agua' | 'luz' | 'manutencao' | 'limpeza' | 'mobilia' | 'outros';
export type ChaveCusto = 'aluguel' | TipoGasto;

export const TIPOS_GASTO: readonly { k: TipoGasto; l: string; g: string; ic: string }[] = [
  { k: 'agua', l: 'Água', g: 'Consumo (água/luz)', ic: '💧' },
  { k: 'luz', l: 'Luz', g: 'Consumo (água/luz)', ic: '💡' },
  { k: 'manutencao', l: 'Manutenção', g: 'Manutenção/Limpeza', ic: '🛠️' },
  { k: 'limpeza', l: 'Limpeza', g: 'Manutenção/Limpeza', ic: '🧹' },
  { k: 'mobilia', l: 'Mobília', g: 'Mobília', ic: '🛏️' },
  { k: 'outros', l: 'Outros', g: 'Outros', ic: '📦' },
];
export const CHAVES_CUSTO: readonly ChaveCusto[] = ['aluguel', 'agua', 'luz', 'manutencao', 'limpeza', 'mobilia', 'outros'];
export const ROTULO_CUSTO: Record<ChaveCusto, string> = {
  aluguel: 'Aluguel', agua: 'Água', luz: 'Luz', manutencao: 'Manutenção', limpeza: 'Limpeza', mobilia: 'Mobília', outros: 'Outros',
};
export const DIAS_AVISO_CONTRATO = 60;

export interface Contrato {
  /** "AAAA-MM-DD" */
  inicio: string | null;
  fim: string | null;
  aluguel: number | null;
  caucao: number | null;
  locador: string | null;
  obs: string | null;
}

export interface Lancamento {
  id: string;
  tipo: TipoGasto;
  competencia: Competencia;
  valor: number;
  obs: string | null;
}

export interface ItemInventario {
  id: string;
  item: string;
  qtd: number;
  estado: 'Bom' | 'Regular' | 'Ruim';
  obs: string | null;
}

export interface GestaoAlojamento {
  contrato: Contrato | null;
  lancamentos: Lancamento[];
  inventario: ItemInventario[];
}

export function tipoGasto(k: string): (typeof TIPOS_GASTO)[number] {
  return TIPOS_GASTO.find((t) => t.k === k) ?? (TIPOS_GASTO[TIPOS_GASTO.length - 1] as (typeof TIPOS_GASTO)[number]);
}

/** "1.234,56", "1234.56", "1.234", "R$ 99" → número. Inválido → 0. Protótipo: parseBRL. */
export function lerValorBRL(entrada: string | number | null | undefined): number {
  if (typeof entrada === 'number') return Number.isFinite(entrada) ? entrada : 0;
  let s = String(entrada ?? '').replace(/[^\d,.-]/g, '');
  if (!s) return 0;
  if (s.includes(',')) s = s.replace(/\./g, '').replace(',', '.');
  else if (/^-?\d{1,3}(\.\d{3})+$/.test(s)) s = s.replace(/\./g, '');
  const n = Number.parseFloat(s);
  return Number.isFinite(n) ? n : 0;
}

function diaJuliano(d: DataCivil): number {
  return Math.floor(Date.UTC(d.ano, d.mes - 1, d.dia) / 86_400_000);
}

export function lerDataIso(iso: string): DataCivil {
  const [a, m, d] = iso.slice(0, 10).split('-');
  return { ano: Number(a), mes: Number(m), dia: Number(d) };
}

/** Dias de hoje até a data (negativo = já passou). */
export function diasAte(iso: string, hoje: DataCivil): number {
  return diaJuliano(lerDataIso(iso)) - diaJuliano(hoje);
}

export type SituacaoContrato =
  | { k: 'sem'; rotulo: 'Sem contrato'; dias: null }
  | { k: 'venc' | 'prox' | 'ok'; rotulo: string; dias: number };

export function situacaoContrato(c: Contrato | null, hoje: DataCivil): SituacaoContrato {
  if (!c || !c.fim) return { k: 'sem', rotulo: 'Sem contrato', dias: null };
  const d = diasAte(c.fim, hoje);
  if (d < 0) return { k: 'venc', rotulo: `Vencido há ${-d} d`, dias: d };
  if (d <= DIAS_AVISO_CONTRATO) return { k: 'prox', rotulo: `Vence em ${d} d`, dias: d };
  return { k: 'ok', rotulo: 'Vigente', dias: d };
}

function iso2(n: number): string {
  return String(n).padStart(2, '0');
}

/**
 * Aluguel que conta no mês: só mês já iniciado e dentro da vigência do contrato.
 * (Mesma comparação de texto do protótipo: "AAAA-MM-31" como último dia.)
 */
export function aluguelDoMes(c: Contrato | null, ano: number, mes: number, hoje: DataCivil): number {
  if (!c || !c.inicio || !((c.aluguel ?? 0) > 0)) return 0;
  if (ano > hoje.ano || (ano === hoje.ano && mes > hoje.mes)) return 0;
  const primeiro = `${ano}-${iso2(mes)}-01`;
  const ultimo = `${ano}-${iso2(mes)}-31`;
  return c.inicio <= ultimo && (!c.fim || c.fim >= primeiro) ? (c.aluguel as number) : 0;
}

export type PorCusto = Record<ChaveCusto, number>;

export function zerado(): PorCusto {
  return { aluguel: 0, agua: 0, luz: 0, manutencao: 0, limpeza: 0, mobilia: 0, outros: 0 };
}

/** Período da lista de alojamentos: um ano inteiro, ou um mês dele. */
export interface Periodo {
  ano: number;
  mes: number | null;
}

export function noPeriodo(c: Competencia, p: Periodo): boolean {
  return c.ano === p.ano && (p.mes === null || c.mes === p.mes);
}

export function rotuloPeriodo(p: Periodo, meses: readonly string[]): string {
  return p.mes ? `${meses[p.mes - 1]}/${p.ano}` : String(p.ano);
}

/** Lançamentos do período, somados por tipo (sem aluguel). Protótipo: gestSoma. */
export function somaDoPeriodo(g: GestaoAlojamento, p: Periodo): { total: number; por: PorCusto } {
  const por = zerado();
  let total = 0;
  for (const l of g.lancamentos) {
    if (!noPeriodo(l.competencia, p)) continue;
    total += l.valor;
    por[tipoGasto(l.tipo).k] += l.valor;
  }
  return { total, por };
}

export interface LinhaAgregada {
  obraId: string;
  obraNome: string;
  alojId: string;
  alojNome: string;
  por: PorCusto;
  total: number;
}

export interface Agregado {
  meses: { por: PorCusto; total: number }[];
  linhas: LinhaAgregada[];
  tot: PorCusto;
  total: number;
}

/** Custos do ano por mês, por alojamento e no total. Protótipo: gestAgg. */
export function agregarAno(
  obras: readonly ObraCadastro[], gestao: (alojId: string) => GestaoAlojamento, ano: number, incluirAluguel: boolean, hoje: DataCivil,
): Agregado {
  const meses = Array.from({ length: 12 }, () => ({ por: zerado(), total: 0 }));
  const linhas: LinhaAgregada[] = [];
  for (const ob of obras) {
    for (const al of ob.alojamentos) {
      const g = gestao(al.id);
      const por = zerado();
      for (const l of g.lancamentos) {
        if (l.competencia.ano !== ano) continue;
        const k = tipoGasto(l.tipo).k;
        por[k] += l.valor;
        (meses[l.competencia.mes - 1] as { por: PorCusto }).por[k] += l.valor;
      }
      if (incluirAluguel) {
        for (let m = 1; m <= 12; m++) {
          const v = aluguelDoMes(g.contrato, ano, m, hoje);
          por.aluguel += v;
          (meses[m - 1] as { por: PorCusto }).por.aluguel += v;
        }
      }
      let t = 0;
      for (const k of CHAVES_CUSTO) t += por[k];
      linhas.push({ obraId: ob.id, obraNome: ob.nome, alojId: al.id, alojNome: al.nome, por, total: t });
    }
  }
  for (const m of meses) {
    let t = 0;
    for (const k of CHAVES_CUSTO) t += m.por[k];
    m.total = t;
  }
  const tot = zerado();
  let total = 0;
  for (const r of linhas) {
    for (const k of CHAVES_CUSTO) tot[k] += r.por[k];
    total += r.total;
  }
  return { meses, linhas, tot, total };
}

/** Anos do filtro: atual, anterior, o selecionado e todos que aparecem em lançamentos ou contratos. */
export function anosDaGestao(hoje: DataCivil, selecionado: number | null, todos: GestaoAlojamento[]): number[] {
  const set = new Set<number>([hoje.ano, hoje.ano - 1]);
  if (selecionado) set.add(selecionado);
  for (const g of todos) {
    for (const l of g.lancamentos) if (l.competencia.ano) set.add(l.competencia.ano);
    for (const d of [g.contrato?.inicio, g.contrato?.fim]) {
      const y = Number.parseInt(String(d ?? '').slice(0, 4), 10);
      if (y > 1990 && y < 2100) set.add(y);
    }
  }
  return [...set].sort((a, b) => b - a);
}

export interface CartoesGestao {
  aluguelVigente: number;
  caucaoTotal: number;
  aVencer: number;
  vencidos: number;
  gastoPeriodo: number;
  /** Total por grupo ("Consumo (água/luz)", …) na ordem dos tipos; só grupos com valor. */
  porGrupo: { grupo: string; valor: number }[];
}

/** Cartões do topo da lista de alojamentos. Protótipo: cardsResumo. */
export function cartoesDaGestao(
  obras: readonly ObraCadastro[], gestao: (alojId: string) => GestaoAlojamento, p: Periodo, hoje: DataCivil,
): CartoesGestao {
  const grupos = new Map<string, number>();
  for (const t of TIPOS_GASTO) grupos.set(t.g, 0);
  const r: CartoesGestao = { aluguelVigente: 0, caucaoTotal: 0, aVencer: 0, vencidos: 0, gastoPeriodo: 0, porGrupo: [] };
  for (const ob of obras) {
    for (const al of ob.alojamentos) {
      const g = gestao(al.id);
      const st = situacaoContrato(g.contrato, hoje);
      if (st.k === 'ok' || st.k === 'prox') r.aluguelVigente += g.contrato?.aluguel ?? 0;
      r.caucaoTotal += g.contrato?.caucao ?? 0;
      if (st.k === 'prox') r.aVencer++;
      if (st.k === 'venc') r.vencidos++;
      const s = somaDoPeriodo(g, p);
      r.gastoPeriodo += s.total;
      for (const t of TIPOS_GASTO) grupos.set(t.g, (grupos.get(t.g) ?? 0) + s.por[t.k]);
    }
  }
  r.porGrupo = [...grupos].filter(([, v]) => v > 0).map(([grupo, valor]) => ({ grupo, valor }));
  return r;
}

export interface KpisDashboard {
  total: number;
  aluguel: number;
  consumoManutencao: number;
  mobilia: number;
  mediaMensal: number;
  mesesConsiderados: number;
  maisCaro: LinhaAgregada | null;
}

/** KPIs do "Dashboard anual". Protótipo: viewDash. */
export function kpisDoDashboard(a: Agregado, ano: number, hoje: DataCivil): KpisDashboard {
  const meses = ano === hoje.ano ? hoje.mes : ano < hoje.ano ? 12 : 0;
  const topo = [...a.linhas].sort((x, y) => y.total - x.total)[0];
  return {
    total: a.total,
    aluguel: a.tot.aluguel,
    consumoManutencao: a.tot.agua + a.tot.luz + a.tot.manutencao + a.tot.limpeza + a.tot.outros,
    mobilia: a.tot.mobilia,
    mediaMensal: meses ? a.total / meses : 0,
    mesesConsiderados: meses,
    maisCaro: topo && topo.total > 0 ? topo : null,
  };
}

/** Competência padrão do formulário de gasto. Protótipo: gestFormComp. */
export function competenciaPadraoDoGasto(p: Periodo, hoje: DataCivil): Competencia {
  if (p.mes) return { ano: p.ano, mes: p.mes };
  if (hoje.ano === p.ano) return competenciaDe(hoje);
  return { ano: p.ano, mes: 1 };
}

/** Lançamentos ordenados por competência (lista do alojamento e CSV de gastos). */
export function ordenarLancamentos(l: readonly Lancamento[]): Lancamento[] {
  return [...l].sort((a, b) => chaveCompetencia(a.competencia) - chaveCompetencia(b.competencia));
}
